/**
 * CLUB CAPABILITIES — what a club has CHOSEN to run (D16.1–D16.8,
 * docs/adr-club-capabilities-and-onboarding-choice.md). Not the plan: a plan says what a
 * club has paid for, this says what it wants to see.
 *
 * THE ONE READING. Every surface asks `capabilitiesOf(org)` of the org load it already has
 * (the workspace's meta read, the public club read) — there is no capability fetch. A club row
 * without a `capabilities` map is a legacy club, or a backend that predates the map: all seven
 * are on (D16.4), so this file is correct against a server that has never heard of it.
 *
 * Capabilities only HIDE. Every condition below is ANDed with whatever role gate already stands;
 * none of them is ever a reason to show something a role would not.
 *
 * The derived conditions live here, by name, so the hide table
 * (docs/ux/2026-10-28-what-will-you-run-here.md §5) is written down in one place.
 */

export const CAPABILITY_KEYS = [
  'court_booking',
  'coaching',
  'programs',
  'memberships',
  'packages',
  'open_play',
  'competitions',
] as const

export type CapabilityKey = (typeof CAPABILITY_KEYS)[number]
export type CapabilityMap = Readonly<Record<CapabilityKey, boolean>>
/** A one-time initialiser sent at club creation (ADR §2) — never stored, never read back. */
export type Preset = 'organizer' | 'coach' | 'club'

const mapOf = (on: readonly CapabilityKey[]): CapabilityMap =>
  Object.freeze(Object.fromEntries(CAPABILITY_KEYS.map((key) => [key, on.includes(key)])) as Record<CapabilityKey, boolean>)

export const ALL_CAPABILITIES: CapabilityMap = mapOf(CAPABILITY_KEYS)

/** What each preset writes (D16.3) — the backend's own table (`clubCapabilities/types.mjs`), read
 *  here only to know a fresh club's map before its first club read answers. */
export const PRESET_CAPABILITIES: Readonly<Record<Preset, CapabilityMap>> = Object.freeze({
  organizer: mapOf(['competitions']),
  coach: mapOf(['coaching', 'programs', 'packages', 'competitions', 'open_play']),
  club: ALL_CAPABILITIES,
})

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value)

/**
 * The club's map. The server always sends the RESOLVED seven-key map, so a key read here is
 * `=== true` or off; an absent or malformed map is the legacy club, all on.
 */
export function capabilitiesOf(org: { capabilities?: unknown } | null | undefined): CapabilityMap {
  const raw = org?.capabilities
  if (!isPlainObject(raw)) return ALL_CAPABILITIES
  return Object.freeze(
    Object.fromEntries(CAPABILITY_KEYS.map((key) => [key, raw[key] === true])) as Record<CapabilityKey, boolean>,
  )
}

/** True when every key is on — a legacy club, or a club that chose `club` and kept it. */
export function allCapabilitiesOn(caps: CapabilityMap): boolean {
  return CAPABILITY_KEYS.every((key) => caps[key])
}

/**
 * THE LANE (brief §6): where a manager lands and which Get Started he meets. Derived from the
 * map, never from a stored preset, so a club that toggles its way into a lane is in it.
 */
export type CapabilityLane = 'club' | 'coach' | 'organizer' | 'rest'

export function capabilityLane(caps: CapabilityMap): CapabilityLane {
  if (caps.court_booking) return 'club'
  if (caps.coaching || caps.programs) return 'coach'
  if (caps.competitions) return 'organizer'
  return 'rest'
}

/* ── derived conditions of the hide table (§5) ─────────────────────────────── */

/** The public Courts strip is drawn at all (§5.1, §5.6): someone can book, or join. */
export const offersCourtStrip = (caps: CapabilityMap): boolean => caps.court_booking || caps.open_play

/**
 * D16.9: a club is OFFERED courts when anything it runs puts something on one — rental, open
 * matches, lessons or clinics. All four off (the organizer preset) = no courts offered anywhere:
 * no "Add courts", no courts line. Existing courts are never hidden by this (D16.5).
 * `competitions`, `memberships` and `packages` do not count.
 */
export const usesCourts = (caps: CapabilityMap): boolean =>
  caps.court_booking || caps.open_play || caps.coaching || caps.programs

/** "Join a match" mode (§5.6): open matches without court rental — free time is not drawn. */
export const joinMatchMode = (caps: CapabilityMap): boolean => !caps.court_booking && caps.open_play

/** A PLAYER posts an open match by booking a cell, so he needs both (§5.6, flag 2). */
export const playerPostsOpenMatch = (caps: CapabilityMap): boolean => caps.court_booking && caps.open_play

/** Club setup › Coaching › Coaches: clinics need coaches too (§5.2). */
export const showsCoachesSetup = (caps: CapabilityMap): boolean => caps.coaching || caps.programs

/** The cancellation notice fieldset: it has a court line and a programme line (§5.1, §5.3). */
export const showsCancellationNotice = (caps: CapabilityMap): boolean => caps.court_booking || caps.programs

/** The public sidebar's `Open full schedule` (§5.1). */
export const opensFullSchedule = (caps: CapabilityMap): boolean =>
  caps.court_booking || caps.open_play || caps.programs

/** The public Academy › Coaches view (§5.2). */
export const offersCoachesView = (caps: CapabilityMap): boolean => caps.coaching || caps.programs

/**
 * The board's signed-out line (§5.10): `Sign in or create an account to {list}.` built from the
 * keys that are on, in this order; `null` when none is — the line is then not drawn.
 */
const SIGNED_OUT_PHRASES: ReadonlyArray<[CapabilityKey, string]> = [
  ['court_booking', 'book a court'],
  ['open_play', 'join an open match'],
  ['programs', 'enrol in a clinic'],
]

export function signedOutHintList(caps: CapabilityMap): string | null {
  const phrases = SIGNED_OUT_PHRASES.filter(([key]) => caps[key]).map(([, phrase]) => phrase)
  if (phrases.length === 0) return null
  if (phrases.length === 1) return phrases[0]
  return `${phrases.slice(0, -1).join(', ')} or ${phrases[phrases.length - 1]}`
}
